import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { User, Calendar, Users } from 'lucide-react';
import api from '../services/api';

const BasicInfoPage = () => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    gender: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validateAge = (dob) => {
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age >= 18;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.firstName.trim()) {
      toast({ title: "Error", description: "First name is required", variant: "destructive" });
      return;
    }
    if (!formData.lastName.trim()) {
      toast({ title: "Error", description: "Last name is required", variant: "destructive" });
      return;
    }
    if (!formData.dateOfBirth) {
      toast({ title: "Error", description: "Date of birth is required", variant: "destructive" });
      return;
    }
    if (!validateAge(formData.dateOfBirth)) {
      toast({ title: "Error", description: "You must be at least 18 years old", variant: "destructive" });
      return;
    }
    if (!formData.gender) {
      toast({ title: "Error", description: "Gender is required", variant: "destructive" });
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/auth/complete-basic-info', {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        dob: formData.dateOfBirth,
        gender: formData.gender
      });

      if (response.data.success) {
        toast({
          title: "Profile Submitted!",
          description: "Your account is now under review.",
        });
        await refreshUser();
        navigate('/account-pending');
      }
    } catch (error) {
      console.error('Basic info submission error:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to submit. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Calculate max date (18 years ago)
  const maxDate = new Date();
  maxDate.setFullYear(maxDate.getFullYear() - 18);
  const maxDateStr = maxDate.toISOString().split('T')[0];

  // Calculate min date (100 years ago)
  const minDate = new Date();
  minDate.setFullYear(minDate.getFullYear() - 100);
  const minDateStr = minDate.toISOString().split('T')[0];

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="text-3xl font-bold text-navy-500 mb-2">qbool</div>
          <p className="text-gray-600">Almost there! Tell us about yourself</p>
        </div>

        {/* Progress Indicator */}
        <div className="mb-8">
          <div className="flex items-center justify-center space-x-2">
            <div className="flex items-center">
              <div className="w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center text-sm font-semibold">✓</div>
              <span className="ml-2 text-sm text-green-600 font-medium">Email</span>
            </div>
            <div className="w-8 h-1 bg-green-500"></div>
            <div className="flex items-center">
              <div className="w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center text-sm font-semibold">✓</div>
              <span className="ml-2 text-sm text-green-600 font-medium">Verify</span>
            </div>
            <div className="w-8 h-1 bg-mint-500"></div>
            <div className="flex items-center">
              <div className="w-8 h-8 rounded-full bg-mint-500 text-white flex items-center justify-center text-sm font-semibold">3</div>
              <span className="ml-2 text-sm text-mint-600 font-medium">Basic Info</span>
            </div>
          </div>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-lg shadow-lg p-6 md:p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
            <User className="w-6 h-6 text-navy-500" />
            Basic Information
          </h2>
          <p className="text-gray-600 mb-6">Step 3 of 3: Enter your basic details</p>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* First Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-transparent"
                placeholder="Enter your first name"
                required
              />
            </div>

            {/* Last Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Last Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-transparent"
                placeholder="Enter your last name"
                required
              />
            </div>

            {/* Date of Birth */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="w-4 h-4 inline mr-1" />
                Date of Birth <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="dateOfBirth"
                value={formData.dateOfBirth}
                onChange={handleInputChange}
                min={minDateStr}
                max={maxDateStr}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-transparent"
                required
              />
              <p className="text-xs text-gray-500 mt-1">You must be at least 18 years old</p>
            </div>

            {/* Gender */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Users className="w-4 h-4 inline mr-1" />
                Gender <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, gender: 'male' })}
                  className={`py-3 px-4 rounded-lg border-2 font-medium transition-all ${
                    formData.gender === 'male'
                      ? 'border-mint-500 bg-mint-50 text-mint-700'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  👨 Male
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, gender: 'female' })}
                  className={`py-3 px-4 rounded-lg border-2 font-medium transition-all ${
                    formData.gender === 'female'
                      ? 'border-mint-500 bg-mint-50 text-mint-700'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  👩 Female
                </button>
              </div>
            </div>

            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <strong>What happens next?</strong><br />
                After submitting, your account will be reviewed by our team (usually within 24-48 hours). 
                Once approved, you can start browsing profiles!
              </p>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-mint-500 text-white py-3 rounded-lg font-semibold hover:bg-mint-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Submitting...' : 'Submit for Review'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BasicInfoPage;
